import React, { useState } from 'react'
import ProcessPage from 'components/ProcessPage'
import CardModal from 'components/CardModal'
import SelectItemCard from '../../../../components/Card/SelectItemCard'
import ActionBoxRow from '../../../../components/ActionBoxRow/ActionBoxRow'
import AugmentationEditorPreview from './AugmentationEditorPreview'
import { FilledAddIcon, CircleIcon, ChevronDownIcon, TickIcon3 } from 'images/common'
import { getSelectedType, mapAugmentationConfiguration, mapAugmentationOptions } from './utils'

const AugmentationEditor = ({ color, onClose, selectedAugmentations, previewImage, onSubmit, setSelectedAugmentations, selectedEditor, setSelectedEditor, blurTypes, setBlurTypes, noiseTypes, setNoiseTypes, cropTypes, setCropTypes }) => {
  const findIndex = selectedAugmentations.findIndex(item => item.name === selectedEditor.name)
  const [selectedOption, setSelectedOption] = useState(selectedEditor)
  const [selectedSequence, setSelectedSequence] = useState(findIndex + 1)
  const [selectedType, setSelectedType] = useState(getSelectedType(selectedOption))
  const [expandedType, setExpandedType] = useState(getSelectedType(selectedOption))
  const [isExpanded, setIsExpanded] = useState(false)

  const onSelectSequence = (name, id, selected) => {
    const findIndex = selectedAugmentations.findIndex(item => item.name === name)
    setSelectedSequence(findIndex + 1)
    setSelectedOption(selected)
    setSelectedEditor(selected)
    setSelectedType(getSelectedType(selected))
    setExpandedType(getSelectedType(selected))
  }

  const onValueChange = (selected, val) => {
    let updateAugmentations = []
    selectedAugmentations.forEach(item => {
      if (item.key === selected.key) {
        setSelectedOption({ ...selected, newValues: val })
        setSelectedEditor({ ...selected, newValues: val })
        updateAugmentations.push({ ...item, newValues: val })
      } else {
        updateAugmentations.push(item)
      }
    })
    setSelectedAugmentations(updateAugmentations)

    if (selected.key === 'augment_blur') {
      const updateBlur = blurTypes.map(type => (
        type.blur_type === val.blur_type ? { ...type, newValue: val.newValue || val.kernel_size, confirmed: val.confirmed } : type
      ))
      setBlurTypes(updateBlur)
    }

    if (selected.key === 'augment_noise') {
      const updateNoise = noiseTypes.map(type => (
        type.noise_type === val.noise_type ? { ...type, newValue: val.newValue || val.noise_level, confirmed: val.confirmed } : type
      ))
      setNoiseTypes(updateNoise)
    }

    if (selected.key === 'augment_crop') {
      const updateCrop = cropTypes.map(type => (
        type.crop_type === val.crop_type ? { ...type, new_min: val.new_min || val.crop_min_ratio, new_max: val.new_max || val.crop_max_ratio, confirmed: val.confirmed } : type
      ))
      setCropTypes(updateCrop)
    }
  }

  const onReset = (item) => {
    if (item.parentKey === 'augment_blur' || item.parentKey === 'augment_noise') {
      onValueChange(selectedOption, { ...item.value, newValue: null, confirmed: false })
    } else if (item.parentKey === 'augment_crop') {
      onValueChange(selectedOption, { ...item.value, new_min: null, new_max: null, confirmed: false })
    } else {
      onValueChange(selectedOption, selectedOption.defaultValues)
    }
  }

  const onTypeClick = (type) => {
    const getKey = type.key
    const updateExpanded = getKey === expandedType ? null : getKey
    setExpandedType(updateExpanded)
    setSelectedType(getKey)
  }

  const onConfirmClick = (item) => {
    let nextType = null
    const findIndex = selectedAugmentations.findIndex(item => item.key === selectedOption.key)
    const nextAugmentation = selectedAugmentations[findIndex + 1]
    onValueChange(selectedOption, { ...item.value, confirmed: true })

    if (item.parentKey === 'augment_blur') {
      const findNext = blurTypes.filter(type => type.blur_type !== item.key).find(type => !type.confirmed)
      if (findNext) {
        nextType = findNext.blur_type
      }
    }
    if (item.parentKey === 'augment_noise') {
      const findNext = noiseTypes.filter(type => type.noise_type !== item.key).find(type => !type.confirmed)
      if (findNext) {
        nextType = findNext.noise_type
      }
    }
    if (item.parentKey === 'augment_crop') {
      const findNext = cropTypes.filter(type => type.crop_type !== item.key).find(type => !type.confirmed)
      if (findNext) {
        nextType = findNext.crop_type
      }
    }

    if (nextAugmentation && !nextType) {
      onSelectSequence(nextAugmentation.name, null, nextAugmentation)
    } else {
      setExpandedType(nextType)
      setSelectedType(nextType)
    }
  }

  const contentFormatter = (row) => {
    if (row.parentKey === 'augment_blur' && row.value.confirmed) {
      return `Intensity ${row.value.newValue} of 10`
    }
    if (row.parentKey === 'augment_noise' && row.value.confirmed) {
      return `Intensity ${row.value.newValue} of 100`
    }
    if (row.parentKey === 'augment_crop' && row.value.confirmed) {
      return `Ratio ${row.value.new_min} - ${row.value.new_max}`
    }
    if (row.value.confirmed) {
      const getConfiguration = mapAugmentationConfiguration({ selected: selectedAugmentations, blurTypes, noiseTypes, cropTypes })
      const findItem = getConfiguration.find(item => item.key === row.key)
      return findItem.content.map((item, index) => <ul key={index}><li>{item}</li></ul>)
    }
  }

  const disableContinue = () => {
    let isDisabled = []
    const getSelectedKey = selectedOption.key

    if (getSelectedKey === 'augment_blur') {
      isDisabled.push({ augment_blur: blurTypes.find(type => type.confirmed) ? true : false })
    } else if (getSelectedKey === 'augment_noise') {
      isDisabled.push({ augment_noise: noiseTypes.find(type => type.confirmed) ? true : false })
    } else if (getSelectedKey === 'augment_crop') {
      isDisabled.push({ augment_crop: cropTypes.find(type => type.confirmed) ? true : false })
    } else {
      isDisabled.push({ [getSelectedKey]: selectedOption.newValues?.confirmed || false })
    }

    return !isDisabled.every(item => Object.values(item)[0])
  }

  const onSubmitClick = () => {
    if (selectedSequence === selectedAugmentations.length) {
      onSubmit()
    } else {
      onSelectSequence(selectedAugmentations[selectedSequence].name, null, selectedAugmentations[selectedSequence])
    }
  }

  return (
    <ProcessPage
      onCancel={onClose}
      noScrollUpdate
      onConfirm={{ label: 'Confirm', onClick: onSubmitClick, color, disabled: disableContinue() }}
      customClass='augmentation-popup'
    >
      {() => (
        <div className='popup__cont'>
          <div className='progress-bar'>
            <div style={{ width: `${selectedSequence / selectedAugmentations.length * 100}%` }} className={`viso-bg-${color}`} />
          </div>
          <div className='login'>
            <div className='headline'>
              <h1 className='headline__title'>Augmentation Editor</h1>
              <div className='headline__subtitle'>
                Fine-tune settings and modes for every augmentation. Preview results in real time.
              </div>
            </div>
            <div className='augmentation-editor'>
              <div className='augmentation-editor__options'>
                <SelectItemCard
                  selectedOption={selectedOption.name}
                  selectedTitle={isExpanded ? `${selectedOption.name} (Current)` : `Augmentation ${selectedSequence} of ${selectedAugmentations.length}`}
                  onSelectOption={onSelectSequence}
                  data={mapAugmentationConfiguration({ selected: selectedAugmentations, blurTypes, noiseTypes, cropTypes })}
                  disableSelected={isExpanded}
                  autoHide={false}
                  imageKey='url'
                  isLight
                  color='label'
                  isLarge
                  onExpand={setIsExpanded}
                />
                <CardModal
                  title={selectedOption.name}
                  subHeader={selectedOption.description}
                  titleClass='modal__title_label'
                  classNames="modal__frame--full"
                  body={(
                    <ActionBoxRow
                      rows={mapAugmentationOptions({ selected: selectedOption, onValueChange, color, blurTypes, noiseTypes, cropTypes })}
                      color={color}
                      isCard
                      action={{
                        type: 'button',
                        expandedIconColor: 'text',
                        expandedIcon: <ChevronDownIcon />
                      }}
                      rowClick={(item) => onTypeClick(item)}
                      isExpanded={(item) => item.key === expandedType}
                      isSelected={(item) => item.key === selectedType}
                      expandedActions={[
                        {
                          label: 'Reset',
                          onClick: onReset,
                          plain: color
                        },
                        {
                          label: 'Confirm',
                          onClick: onConfirmClick,
                          light: color
                        }
                      ]}
                      cellDefs={[
                        {
                          key: 'name',
                          icon: (item) => (item.value.confirmed) ? <TickIcon3 /> : <CircleIcon />,
                          iconColor: (item) => (item.value.confirmed) ? 'success' : 'label',
                          selectedIconColor: (item) => (item.value.confirmed) ? 'success' : color,
                          selectedIcon: (item) => (item.value.confirmed) ? <TickIcon3 /> : <FilledAddIcon />,
                          expandedClassName: 'viso-font-medium viso-text-text',
                          className: (item) => (item.value.confirmed) ? 'viso-font-medium viso-text-text' : ''
                        },
                        {
                          key: 'content',
                          formatter: (value, row) => contentFormatter(row),
                          expandedClassName: 'visibility-hidden',
                          textColor: 'label'
                        }
                      ]}
                    />
                  )}
                />
              </div>
              <div className='augmentation-editor__preview'>
                <AugmentationEditorPreview selectedOption={selectedOption} previewImage={previewImage} color={color} />
              </div>
            </div>
          </div>
        </div>
      )}
    </ProcessPage>
  )
}

export default AugmentationEditor